import { useEffect, useState, type ReactNode } from "react";
import { Crosshair } from "lucide-react";
import {
  SourceBoxSchema,
  documentPagePicturePath,
  type SourceBox,
} from "@gb/contracts";
import { fetchAuthedBlob } from "@/api/client";

/**
 * The region a look read, drawn on the picture it read (2026-09-26, Phase 5
 * slice V; `document-ai/0026` §12, R63 and R97).
 *
 * A region is fractions of the picture's width and height from its top-left
 * corner (`SourceBox`), so it needs no pixel size, and it travels in the
 * viewer's address as `&box=x,y,w,h`.
 *
 * **The box is drawn on a wrapper that is exactly the picture's size.** A
 * picture shown with `object-contain` is letterboxed inside its element, and a
 * box placed in percentages of the element would sit off the picture by the
 * width of the bars. So the picture is shown at its own aspect ratio, in an
 * inline block that shrinks to fit it, and the box is placed on that block.
 */

/** `x,y,w,h`, each to four places: a tenth of a millimetre on an A0 sheet. */
export function formatBoxParam(box: SourceBox): string {
  return [box.x, box.y, box.w, box.h]
    .map((value) => String(Math.round(value * 10_000) / 10_000))
    .join(",");
}

/**
 * The region in an address, or null. A value that is not four fractions, or
 * that has no area, is no region: the viewer then opens at the page as before.
 */
export function parseBoxParam(value: string | null): SourceBox | null {
  if (!value) return null;
  const parts = value.split(",");
  if (parts.length !== 4 || parts.some((part) => part.trim() === ""))
    return null;
  const [x, y, w, h] = parts.map(Number);
  const parsed = SourceBoxSchema.safeParse({ x, y, w, h });
  if (!parsed.success || parsed.data.w === 0 || parsed.data.h === 0)
    return null;
  return parsed.data;
}

/** The outline of the region, placed on a `relative` parent the size of the picture. */
export function RegionOverlay({ box }: { box: SourceBox }) {
  // A box a model gave can run past the edge by a rounding; it is clipped to
  // the picture rather than drawn outside it.
  const width = Math.min(box.w, 1 - box.x);
  const height = Math.min(box.h, 1 - box.y);
  return (
    <span
      data-testid="region-overlay"
      aria-hidden
      className="pointer-events-none absolute rounded-[2px] border-[2.5px] border-brand bg-brand/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)]"
      style={{
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        width: `${width * 100}%`,
        height: `${height * 100}%`,
      }}
    />
  );
}

/**
 * A picture with the region drawn on it. The picture inside keeps its own
 * aspect ratio (`block h-auto w-auto max-w-full`, never `object-contain`), so
 * this block is exactly its size.
 */
export function RegionFrame({
  box,
  children,
}: {
  box: SourceBox;
  children: ReactNode;
}) {
  return (
    <span
      data-testid="region-frame"
      className="relative inline-block max-w-full overflow-hidden align-top"
    >
      {children}
      <RegionOverlay box={box} />
    </span>
  );
}

/**
 * "Cited region": the page's picture with the region on it, above a PDF.
 *
 * The PDF is in the browser's own viewer, in a frame nothing can draw over
 * (`architecture/0008`), so the region is shown on a picture of the page
 * beside it. The picture comes from the page picture route. A page that has
 * no picture, or one that fails to load, shows **no panel**: the viewer still
 * opens at the page, and a grey box saying "no picture" would tell the reader
 * nothing they can act on.
 */
export function CitedRegionPanel({
  projectId,
  documentId,
  page,
  box,
}: {
  projectId: string;
  documentId: string;
  page: number;
  box: SourceBox;
}) {
  const url = usePagePicture(
    documentPagePicturePath(projectId, documentId, page),
  );
  if (!url) return null;

  return (
    <section
      aria-label="Cited region"
      className="overflow-hidden rounded-card-lg border-[1.5px] border-brand/40 bg-surface"
    >
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Crosshair size={14} className="text-brand" aria-hidden />
        <span className="font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase">
          Cited region
        </span>
        <span className="text-[0.78rem] text-muted">· page {page}</span>
      </header>
      <div className="flex justify-center bg-surface-sunken p-3">
        <RegionFrame box={box}>
          <img
            src={url}
            alt={`Page ${page}, with the cited region marked`}
            className="block h-auto max-h-[60vh] w-auto max-w-full"
          />
        </RegionFrame>
      </div>
    </section>
  );
}

/**
 * The page picture as a blob URL, or null while it loads and when it fails.
 * Revoked when the page changes or the caller goes, as `AuthedImage` does.
 * `path` is a page picture route (`documentPagePicturePath`).
 */
export function usePagePicture(path: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let gone = false;
    let objectUrl: string | null = null;
    setUrl(null);
    fetchAuthedBlob(path)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (gone) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      })
      .catch(() => {
        // No panel. The viewer below still opens at the page.
      });
    return () => {
      gone = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return url;
}
